import { AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react'
import { useKyc } from '@/src/contexts/KycContext'
import { useNavigate } from 'react-router-dom'

export function KycStatusBanner() {
  const { kycStatus, loading } = useKyc()
  const navigate = useNavigate()

  // Don't show banner if loading or no status
  if (loading || !kycStatus) return null
  
  // Don't show if already verified
  if (kycStatus.verified) return null
  
  // Show rejection alert if rejected
  if (kycStatus.status === 'rejected' && kycStatus.rejectionReason) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <XCircle className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-red-800">
              การยืนยันตัวตนไม่ผ่าน
            </h3>
            <div className="mt-2 text-sm text-red-700">
              <p className="font-medium mb-1">เหตุผล:</p>
              <p>{kycStatus.rejectionReason}</p>
            </div>
            {kycStatus.verifiedType && (
              <div className="mt-2 text-xs text-red-600">
                ประเภทการยืนยัน: {kycStatus.verifiedType === 'individual' ? 'บุคคลธรรมดา' : 'นิติบุคคล'}
              </div>
            )}
            <div className="mt-4">
              <button
                onClick={() => navigate(`/auth/verify/${kycStatus.verifiedType}`)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                แก้ไขและส่งใหม่
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  // Only show pending status if submitted but not rejected
  if (!kycStatus.submitted || kycStatus.status !== 'pending') return null

  // Show pending verification message
  return (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <Clock className="h-5 w-5 text-yellow-400" />
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-yellow-800">
            กำลังตรวจสอบข้อมูล KYC
          </h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p>
              ขณะนี้ทางเจ้าหน้าที่กำลังตรวจสอบข้อมูลการยืนยันตัวตนของคุณ
              คุณสามารถใช้งานระบบได้ตามปกติ แต่บางฟังก์ชันอาจถูกจำกัดจนกว่าการยืนยันตัวตนจะเสร็จสมบูรณ์
            </p>
          </div>
          {kycStatus.verifiedType && (
            <div className="mt-2 text-xs text-yellow-600">
              ประเภทการยืนยัน: {kycStatus.verifiedType === 'individual' ? 'บุคคลธรรมดา' : 'นิติบุคคล'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface KycVerificationAlertProps {
  onVerifyClick?: () => void
}

export function KycVerificationAlert({ onVerifyClick }: KycVerificationAlertProps) {
  const { kycStatus, loading } = useKyc()

  if (loading || !kycStatus) return null

  // Show success message if verified
  if (kycStatus.verified) {
    return (
      <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <CheckCircle className="h-5 w-5 text-green-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              ยืนยันตัวตนสำเร็จ
            </h3>
            <div className="mt-1 text-sm text-green-700">
              <p>การยืนยันตัวตนของคุณเสร็จสมบูรณ์แล้ว คุณสามารถใช้งานฟีเจอร์ทั้งหมดได้</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show pending verification message
  return (
    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <AlertCircle className="h-5 w-5 text-blue-400" />
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-blue-800">
            ต้องการการยืนยันตัวตน (KYC)
          </h3>
          <div className="mt-2 text-sm text-blue-700">
            <p>
              เพื่อใช้งานฟีเจอร์ทั้งหมดและออกใบกำกับภาษี กรุณายืนยันตัวตนของคุณ
            </p>
          </div>
          {onVerifyClick && (
            <div className="mt-4">
              <button
                onClick={onVerifyClick}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                ยืนยันตัวตนตอนนี้
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
